import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-csir';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface CSIRFormData {
    category: string;
    csirNotableCases: string;
    csirCasesInReview: number;
    csirOpenPlumaBolosPEs: string;
    csirOpenSimTs: string;
    csirOpenAlertMediaMessages: string;
    csirUpcomingCalls: string;
    csirStaffingFlags: string;
    csirIssuesFlags: string;
    csirNextSupervisor: string;
    // Tiptap ProseMirror JSON mirrors of the CSIR rich-text fields (csirCasesInReview
    // and csirNextSupervisor stay as-is).
    csirNotableCasesJson?: Record<string, any>;
    csirOpenPlumaBolosPEsJson?: Record<string, any>;
    csirOpenSimTsJson?: Record<string, any>;
    csirOpenAlertMediaMessagesJson?: Record<string, any>;
    csirUpcomingCallsJson?: Record<string, any>;
    csirStaffingFlagsJson?: Record<string, any>;
    csirIssuesFlagsJson?: Record<string, any>;
}

const defaultForm: CSIRFormData = {
    category: 'CSIR',
    csirNotableCases: '',
    csirCasesInReview: 0,
    csirOpenPlumaBolosPEs: '',
    csirOpenSimTs: '',
    csirOpenAlertMediaMessages: '',
    csirUpcomingCalls: '',
    csirStaffingFlags: '',
    csirIssuesFlags: '',
    csirNextSupervisor: '',
};

const CSIRPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<CSIRFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof CSIRFormData, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.csirpassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('CSIR');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'CSIR',
            csirNotableCases: b.csirNotableCases || '',
            csirCasesInReview: b.csirCasesInReview || 0,
            csirOpenPlumaBolosPEs: b.csirOpenPlumaBolosPEs || '',
            csirOpenSimTs: b.csirOpenSimTs || '',
            csirOpenAlertMediaMessages: b.csirOpenAlertMediaMessages || '',
            csirUpcomingCalls: b.csirUpcomingCalls || '',
            csirStaffingFlags: b.csirStaffingFlags || '',
            csirIssuesFlags: b.csirIssuesFlags || '',
            csirNextSupervisor: b.csirNextSupervisor || '',
            csirNotableCasesJson: b.csirNotableCasesJson,
            csirOpenPlumaBolosPEsJson: b.csirOpenPlumaBolosPEsJson,
            csirOpenSimTsJson: b.csirOpenSimTsJson,
            csirOpenAlertMediaMessagesJson: b.csirOpenAlertMediaMessagesJson,
            csirUpcomingCallsJson: b.csirUpcomingCallsJson,
            csirStaffingFlagsJson: b.csirStaffingFlagsJson,
            csirIssuesFlagsJson: b.csirIssuesFlagsJson,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('CSIR Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'CSIR Passdown updated' : 'CSIR Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Used for the fields kept as-is: # of Cases in Review (number) and Next
    // Supervisor on Shift (single-line text).
    const renderField = (label: string, value?: string | number) => {
        if (!value && value !== 0) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                {/* Both values are single-line (a count and a supervisor name), so
                    render as escaped plain text — no dangerouslySetInnerHTML. */}
                <p>{value}</p>
            </div>
        );
    };

    // Render a CSIR rich-text field read-only. Prefers the Tiptap JSON when present
    // (rendered safely via RichTextContent); otherwise falls back to the legacy
    // plain-text string so older records still render as before.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    // All fields required except the optional Issues/flags field. The rich-text
    // fields are checked via richTextToPlain; Next Supervisor stays a plain string.
    const hasContent = [
        form.csirNotableCases, form.csirOpenPlumaBolosPEs, form.csirOpenSimTs,
        form.csirOpenAlertMediaMessages, form.csirUpcomingCalls, form.csirStaffingFlags,
    ].every(val => !!richTextToPlain(val)) && !!form.csirNextSupervisor.trim();
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>Notable Cases{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.csirNotableCasesJson ?? formatPlainTextToHtml(form.csirNotableCases || '')}
                            placeholder="Notable cases..."
                            onChangeText={(text) => updateField('csirNotableCases', text)}
                            onChangeJson={(json) => updateField('csirNotableCasesJson', json)}
                        />
                    </FormField>

                    <div className="passdown-form__staffing-grid">
                        <div className="passdown-form__staffing-row">
                            <FormField label="# of Cases in Review" />
                            <Input
                                inputMode="numeric"
                                type="number"
                                value={String(form.csirCasesInReview || 0)}
                                onChange={({ detail }) => updateField('csirCasesInReview', Math.max(0, parseInt(detail.value) || 0))}
                            />
                        </div>
                    </div>

                    <FormField label={<>Open Pluma Products/BOLOs/PEs{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirOpenPlumaBolosPEsJson ?? formatPlainTextToHtml(form.csirOpenPlumaBolosPEs || '')}
                            placeholder="Open Pluma Products, BOLOs, PEs..."
                            onChangeText={(text) => updateField('csirOpenPlumaBolosPEs', text)}
                            onChangeJson={(json) => updateField('csirOpenPlumaBolosPEsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Open SIM-Ts{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirOpenSimTsJson ?? formatPlainTextToHtml(form.csirOpenSimTs || '')}
                            placeholder="Open SIM-Ts..."
                            onChangeText={(text) => updateField('csirOpenSimTs', text)}
                            onChangeJson={(json) => updateField('csirOpenSimTsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Open AlertMedia Messages{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirOpenAlertMediaMessagesJson ?? formatPlainTextToHtml(form.csirOpenAlertMediaMessages || '')}
                            placeholder="Open AlertMedia messages..."
                            onChangeText={(text) => updateField('csirOpenAlertMediaMessages', text)}
                            onChangeJson={(json) => updateField('csirOpenAlertMediaMessagesJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Upcoming Triage/IMT/CMT Calls{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirUpcomingCallsJson ?? formatPlainTextToHtml(form.csirUpcomingCalls || '')}
                            placeholder="Upcoming triage, IMT, CMT calls..."
                            onChangeText={(text) => updateField('csirUpcomingCalls', text)}
                            onChangeJson={(json) => updateField('csirUpcomingCallsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Staffing Flags (call-offs, coverage risks){requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirStaffingFlagsJson ?? formatPlainTextToHtml(form.csirStaffingFlags || '')}
                            placeholder="Call-offs, coverage risks..."
                            onChangeText={(text) => updateField('csirStaffingFlags', text)}
                            onChangeJson={(json) => updateField('csirStaffingFlagsJson', json)}
                        />
                    </FormField>

                    <FormField label="Issues/Flags (optional)" stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.csirIssuesFlagsJson ?? formatPlainTextToHtml(form.csirIssuesFlags || '')}
                            placeholder="Issues or flags..."
                            onChangeText={(text) => updateField('csirIssuesFlags', text)}
                            onChangeJson={(json) => updateField('csirIssuesFlagsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Next Supervisor on Shift{requiredMark()}</>} stretch>
                        <Input
                            placeholder="Next supervisor on shift..."
                            value={form.csirNextSupervisor ?? ''}
                            onChange={({ detail }) => updateField('csirNextSupervisor', detail.value)}
                        />
                    </FormField>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update CSIR Passdown' : 'Submit CSIR Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No CSIR passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('csirShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.csirNotableCases) || richTextToPlain(b0.csirOpenPlumaBolosPEs)} ...</p>}

                            {activeExpanded && (
                                <div className="passdown-entry__fields-grid">
                                    {renderRichField('Notable Cases', b0.csirNotableCasesJson, b0.csirNotableCases)}
                                    {renderField('# of Cases in Review', b0.csirCasesInReview)}
                                    {renderRichField('Open Pluma Products/BOLOs/PEs', b0.csirOpenPlumaBolosPEsJson, b0.csirOpenPlumaBolosPEs)}
                                    {renderRichField('Open SIM-Ts', b0.csirOpenSimTsJson, b0.csirOpenSimTs)}
                                    {renderRichField('Open AlertMedia Messages', b0.csirOpenAlertMediaMessagesJson, b0.csirOpenAlertMediaMessages)}
                                    {renderRichField('Upcoming Triage/IMT/CMT Calls', b0.csirUpcomingCallsJson, b0.csirUpcomingCalls)}
                                    {renderRichField('Staffing Flags', b0.csirStaffingFlagsJson, b0.csirStaffingFlags)}
                                    {renderRichField('Issues/Flags', b0.csirIssuesFlagsJson, b0.csirIssuesFlags)}
                                    {renderField('Next Supervisor on Shift', b0.csirNextSupervisor)}
                                </div>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 CSIR PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((b, idx) => {
                                    const isExpanded = expandedId === b.passdownId;
                                    return (
                                        <span key={b.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={b.createdAt} /> &middot; by {b.createdBy}
                                                            { b.updatedAt && b.updatedAt !== b.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b.updatedAt} /> &middot; by {b.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('csirShiftPassdown', b.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(b)}
                                                                    onDelete={() => handleDeleteClick(b)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : b.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            <div className="passdown-entry__fields-grid">
                                                                {renderRichField('Notable Cases', b.csirNotableCasesJson, b.csirNotableCases)}
                                                                {renderField('# of Cases in Review', b.csirCasesInReview)}
                                                                {renderRichField('Open Pluma Products/BOLOs/PEs', b.csirOpenPlumaBolosPEsJson, b.csirOpenPlumaBolosPEs)}
                                                                {renderRichField('Open SIM-Ts', b.csirOpenSimTsJson, b.csirOpenSimTs)}
                                                                {renderRichField('Open AlertMedia Messages', b.csirOpenAlertMediaMessagesJson, b.csirOpenAlertMediaMessages)}
                                                                {renderRichField('Upcoming Triage/IMT/CMT Calls', b.csirUpcomingCallsJson, b.csirUpcomingCalls)}
                                                                {renderRichField('Staffing Flags', b.csirStaffingFlagsJson, b.csirStaffingFlags)}
                                                                {renderRichField('Issues/Flags', b.csirIssuesFlagsJson, b.csirIssuesFlags)}
                                                                {renderField('Next Supervisor on Shift', b.csirNextSupervisor)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete CSIR Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this CSIR passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default CSIRPassdown;
